import React from "react";
import { useDispatch } from "react-redux";

import User from "../../models/user.model";
import { Institution, InstitutionDraft } from "../../models/institution.model";
import { GetUsers } from "./store/admin.actions";
import styles from "./admin-theme.module.css";
import {
  AdminUsersDirectoryMeta,
  AdminUsersDirectoryQuery,
  createInstitutionRequest,
  getInstitutionsRequest,
  getUsersDirectoryRequest,
  updateAdminUserSettingsRequest,
} from "./store/admin.api";

type UserSettingsDraft = {
  curator: boolean;
  institutionId: string;
  galleryLimit: string;
};

const CURATOR_NOTE =
  "Curators must belong to one institution. Sketchfab SRS quota is shared at the institution level, while ADMIN/SUPERADMIN remains unlimited.";

const EMPTY_INSTITUTION_DRAFT: InstitutionDraft = {
  name: "",
  edmProviderName: "",
  sketchfabImportLimit: 10,
  active: true,
};

const CREATE_INSTITUTION_NAME_ID = "create-institution-name";
const CREATE_INSTITUTION_PROVIDER_ID = "create-institution-provider";
const CREATE_INSTITUTION_LIMIT_ID = "create-institution-limit";
const DEFAULT_DIRECTORY_PAGE = 1;
const DEFAULT_DIRECTORY_LIMIT = 25;
const DEFAULT_DIRECTORY_SORT = "name:asc";
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const SORT_OPTIONS = [
  { value: "name:asc", label: "Name (A-Z)" },
  { value: "email:asc", label: "Email (A-Z)" },
  { value: "createdAt:desc", label: "Newest" },
  { value: "createdAt:asc", label: "Oldest" },
];

const getUserRoles = (user: Partial<User>): string[] => {
  if (Array.isArray(user.roles) && user.roles.length > 0) {
    return user.roles;
  }

  if (user.role) {
    return [user.role];
  }

  return [];
};

const isAdminRole = (roles: string[]) =>
  roles.includes("ADMIN") || roles.includes("SUPERADMIN") || roles.includes("GALLERY");
const isSellerRole = (roles: string[]) => roles.includes("SELLER");
const resolveAdminInstitutionName = (user: Partial<User>) => user.institution?.name || "Artetra";

const normalizeInstitutionId = (value: unknown): string =>
  typeof value === "string" ? value : "";

const toInstitutionItems = (response: any): Institution[] => {
  const items = response?.data?.data?.items;
  return Array.isArray(items) ? items : [];
};

const buildDefaultDirectoryMeta = (): AdminUsersDirectoryMeta => ({
  total: 0,
  page: DEFAULT_DIRECTORY_PAGE,
  limit: DEFAULT_DIRECTORY_LIMIT,
  totalPages: 1,
  search: null,
  sortBy: "name",
  sortDirection: "asc",
  paginated: true,
});

const buildDirectoryQuery = (
  page: number,
  limit: number,
  search: string,
  sortBy: AdminUsersDirectoryMeta["sortBy"],
  sortDirection: AdminUsersDirectoryMeta["sortDirection"]
): AdminUsersDirectoryQuery => {
  const query: AdminUsersDirectoryQuery = {
    page,
    limit,
    sortBy,
    sortDirection,
  };

  if (search.trim().length > 0) {
    query.search = search.trim();
  }

  return query;
};

const toUsersDirectoryPayload = (
  response: any,
  fallbackQuery: AdminUsersDirectoryQuery
): { items: User[]; meta: AdminUsersDirectoryMeta } => {
  const items = Array.isArray(response?.data?.data?.items)
    ? response.data.data.items
    : Array.isArray(response?.data?.users)
      ? response.data.users
      : [];
  const meta = response?.data?.meta || {};
  const fallbackMeta = buildDefaultDirectoryMeta();
  const total = typeof meta.total === "number" ? meta.total : items.length;
  const resolvedLimit =
    typeof meta.limit === "number" ? meta.limit : fallbackQuery.limit || DEFAULT_DIRECTORY_LIMIT;

  return {
    items,
    meta: {
      total,
      page: typeof meta.page === "number" ? meta.page : fallbackQuery.page || DEFAULT_DIRECTORY_PAGE,
      limit: resolvedLimit,
      totalPages:
        typeof meta.totalPages === "number"
          ? meta.totalPages
          : Math.max(1, Math.ceil(total / Math.max(1, resolvedLimit))),
      search: typeof meta.search === "string" || meta.search === null ? meta.search : fallbackMeta.search,
      sortBy: meta.sortBy || fallbackQuery.sortBy || fallbackMeta.sortBy,
      sortDirection: meta.sortDirection || fallbackQuery.sortDirection || fallbackMeta.sortDirection,
      paginated: meta.paginated === true,
    },
  };
};

const getUserSettingsDraft = (user: User): UserSettingsDraft => ({
  curator: user.curator === true,
  institutionId: normalizeInstitutionId(user.institutionId),
  galleryLimit: Number.isInteger(user.galleryLimit) && user.galleryLimit >= 1
    ? String(user.galleryLimit)
    : "",
});

const isUserSettingsDraftDirty = (draft: UserSettingsDraft | undefined, user: User): boolean => {
  if (!draft) {
    return false;
  }

  const serverDraft = getUserSettingsDraft(user);
  return draft.curator !== serverDraft.curator
    || draft.institutionId !== serverDraft.institutionId
    || draft.galleryLimit !== serverDraft.galleryLimit;
};

const mergeUserSettingsDrafts = (
  current: Record<string, UserSettingsDraft>,
  nextUsers: User[]
): Record<string, UserSettingsDraft> => {
  const nextDrafts = { ...current };

  nextUsers.forEach((user) => {
    if (!nextDrafts[user._id] || !isUserSettingsDraftDirty(nextDrafts[user._id], user)) {
      nextDrafts[user._id] = getUserSettingsDraft(user);
    }
  });

  return nextDrafts;
};

const isValidGalleryLimit = (value: string): boolean => {
  const parsed = Number(value);
  return value.trim().length > 0 && Number.isInteger(parsed) && parsed >= 1;
};

const buildVisiblePageNumbers = (page: number, totalPages: number): number[] => {
  if (totalPages <= 1) {
    return [1];
  }

  const start = Math.max(1, page - 1);
  const end = Math.min(totalPages, page + 1);
  const numbers: number[] = [];

  for (let index = start; index <= end; index += 1) {
    numbers.push(index);
  }

  return numbers;
};

export const UserList = () => {
  const dispatch = useDispatch();
  const latestDirectoryRequestId = React.useRef(0);
  const [users, setUsers] = React.useState<User[]>([]);
  const [usersMeta, setUsersMeta] = React.useState<AdminUsersDirectoryMeta>(buildDefaultDirectoryMeta);
  const [usersLoading, setUsersLoading] = React.useState<boolean>(true);
  const [usersError, setUsersError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState<number>(DEFAULT_DIRECTORY_PAGE);
  const [limit, setLimit] = React.useState<number>(DEFAULT_DIRECTORY_LIMIT);
  const [searchInput, setSearchInput] = React.useState<string>("");
  const [directorySearch, setDirectorySearch] = React.useState<string>("");
  const [sortValue, setSortValue] = React.useState<string>(DEFAULT_DIRECTORY_SORT);
  const [sortBy, setSortBy] = React.useState<AdminUsersDirectoryMeta["sortBy"]>("name");
  const [sortDirection, setSortDirection] = React.useState<AdminUsersDirectoryMeta["sortDirection"]>("asc");
  const [drafts, setDrafts] = React.useState<Record<string, UserSettingsDraft>>({});
  const [institutions, setInstitutions] = React.useState<Institution[]>([]);
  const [savingUserId, setSavingUserId] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [institutionsLoading, setInstitutionsLoading] = React.useState<boolean>(true);
  const [institutionCreatorTargetUserId, setInstitutionCreatorTargetUserId] = React.useState<string | null>(null);
  const [institutionDraft, setInstitutionDraft] = React.useState<InstitutionDraft>(EMPTY_INSTITUTION_DRAFT);
  const [institutionSaving, setInstitutionSaving] = React.useState<boolean>(false);

  const loadInstitutions = React.useCallback(async () => {
    setInstitutionsLoading(true);
    const response = await getInstitutionsRequest();
    setInstitutionsLoading(false);

    if (response.data?.status) {
      setInstitutions(toInstitutionItems(response));
      return;
    }

    setInstitutions([]);
  }, []);

  const loadUsersDirectory = React.useCallback(async (query: AdminUsersDirectoryQuery) => {
    const requestId = latestDirectoryRequestId.current + 1;
    latestDirectoryRequestId.current = requestId;
    setUsersLoading(true);
    setUsersError(null);
    const response = await getUsersDirectoryRequest(query);

    if (latestDirectoryRequestId.current !== requestId) {
      return;
    }

    setUsersLoading(false);

    if (!response.data?.status) {
      setUsers([]);
      setUsersMeta({
        ...buildDefaultDirectoryMeta(),
        page: query.page || DEFAULT_DIRECTORY_PAGE,
        limit: query.limit || DEFAULT_DIRECTORY_LIMIT,
        search: query.search || null,
        sortBy: query.sortBy || "name",
        sortDirection: query.sortDirection || "asc",
      });
      setUsersError(response.data?.msg || response.data?.error?.message || "Failed to load users");
      return;
    }

    const payload = toUsersDirectoryPayload(response, query);
    setUsers(payload.items);
    setUsersMeta(payload.meta);
    setDrafts((current) => mergeUserSettingsDrafts(current, payload.items));
  }, []);

  React.useEffect(() => {
    loadInstitutions();
  }, [loadInstitutions]);

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDirectorySearch(searchInput.trim());
      setPage(DEFAULT_DIRECTORY_PAGE);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  React.useEffect(() => {
    const query = buildDirectoryQuery(page, limit, directorySearch, sortBy, sortDirection);
    loadUsersDirectory(query);
  }, [directorySearch, limit, loadUsersDirectory, page, sortBy, sortDirection]);

  const handleUserSettingsDraftChange = (userId: string, patch: Partial<UserSettingsDraft>) => {
    setSaveError(null);
    setDrafts((current) => ({
      ...current,
      [userId]: {
        ...(current[userId] || { curator: false, institutionId: "", galleryLimit: "" }),
        ...patch,
      },
    }));
  };

  const handleSave = async (user: User) => {
    const draft = drafts[user._id];
    if (!draft) {
      return;
    }

    if (!isValidGalleryLimit(draft.galleryLimit)) {
      setSaveError("Gallery Limit must be a whole number of at least 1.");
      return;
    }

    const roles = getUserRoles(user);
    const sellerRow = isSellerRole(roles) && !isAdminRole(roles);
    if (sellerRow && draft.curator && !draft.institutionId) {
      setSaveError("Institution assignment is required when curator access is enabled.");
      return;
    }

    setSavingUserId(user._id);
    setSaveError(null);
    const response = await updateAdminUserSettingsRequest(user._id, {
      galleryLimit: Number(draft.galleryLimit),
      ...(sellerRow
        ? { curator: draft.curator, institutionId: draft.institutionId || null }
        : {}),
    });
    setSavingUserId(null);

    if (response.data?.status) {
      const responseUser = (response.data?.data?.user || response.data?.user) as User;
      const updatedUser = {
        ...responseUser,
        galleryUsage: Number.isInteger(responseUser.galleryUsage)
          ? responseUser.galleryUsage
          : user.galleryUsage,
      } as User;
      setUsers((current) =>
        current.map((entry) => (entry._id === updatedUser._id ? updatedUser : entry))
      );
      setDrafts((current) => ({
        ...current,
        [updatedUser._id]: getUserSettingsDraft(updatedUser),
      }));
      dispatch(GetUsers());
      return;
    }

    setSaveError(response.data?.error?.message || response.data?.msg || "Failed to update user settings");
  };

  const resetInstitutionCreator = () => {
    setInstitutionCreatorTargetUserId(null);
    setInstitutionDraft(EMPTY_INSTITUTION_DRAFT);
    setInstitutionSaving(false);
  };

  const handleCreateInstitution = async () => {
    if (!institutionCreatorTargetUserId) {
      return;
    }

    setSaveError(null);
    setInstitutionSaving(true);
    const response = await createInstitutionRequest(institutionDraft);
    setInstitutionSaving(false);

    if (!response.data?.status) {
      setSaveError(response.data?.msg || response.data?.error?.message || "Failed to create institution");
      return;
    }

    await loadInstitutions();
    const createdInstitutionId = response.data?.data?._id;
    if (createdInstitutionId) {
      handleUserSettingsDraftChange(institutionCreatorTargetUserId, {
        institutionId: createdInstitutionId,
        curator: true,
      });
    }
    resetInstitutionCreator();
  };

  const total = usersMeta.total;
  const showingFrom = total === 0 ? 0 : (usersMeta.page - 1) * usersMeta.limit + 1;
  const showingTo = total === 0 ? 0 : showingFrom + users.length - 1;
  const pageNumbers = buildVisiblePageNumbers(usersMeta.page, usersMeta.totalPages);

  return (
    <div className={`container ${styles.adminUsersDirectoryShell}`}>
      {saveError && <div className="text-danger small mb-2" aria-live="polite">{saveError}</div>}
      {usersError && <div className="text-danger small mb-2" aria-live="polite">{usersError}</div>}

      {institutionCreatorTargetUserId && (
        <div className="card bg-dark text-white border-secondary mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <div className="fw-semibold">Create Institution</div>
                <div className="small text-white-50">
                  Create the institution here, then it will be assigned to the curator draft automatically.
                </div>
              </div>
              <button
                className={`btn btn-sm ${styles.darkActionButton}`}
                onClick={resetInstitutionCreator}
                disabled={institutionSaving}
              >
                Cancel
              </button>
            </div>
            <div className="row g-3">
              <div className="col-md-4">
                <label htmlFor={CREATE_INSTITUTION_NAME_ID} className="form-label text-white-50 small">
                  Institution Name
                </label>
                <input
                  id={CREATE_INSTITUTION_NAME_ID}
                  className={`form-control ${styles.lightInput}`}
                  value={institutionDraft.name}
                  onChange={(event) => setInstitutionDraft((current) => ({ ...current, name: event.target.value }))}
                  disabled={institutionSaving}
                />
              </div>
              <div className="col-md-4">
                <label htmlFor={CREATE_INSTITUTION_PROVIDER_ID} className="form-label text-white-50 small">
                  Provider
                </label>
                <input
                  id={CREATE_INSTITUTION_PROVIDER_ID}
                  className={`form-control ${styles.lightInput}`}
                  value={institutionDraft.edmProviderName}
                  onChange={(event) =>
                    setInstitutionDraft((current) => ({ ...current, edmProviderName: event.target.value }))
                  }
                  disabled={institutionSaving}
                />
              </div>
              <div className="col-md-2">
                <label htmlFor={CREATE_INSTITUTION_LIMIT_ID} className="form-label text-white-50 small">
                  Shared SRS Limit
                </label>
                <input
                  id={CREATE_INSTITUTION_LIMIT_ID}
                  className={`form-control ${styles.lightInput}`}
                  type="number"
                  min={1}
                  max={250}
                  value={institutionDraft.sketchfabImportLimit}
                  onChange={(event) =>
                    setInstitutionDraft((current) => ({
                      ...current,
                      sketchfabImportLimit: Number(event.target.value || 0),
                    }))
                  }
                  disabled={institutionSaving}
                />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <div className="form-check mb-2">
                  <input
                    id="create-institution-active"
                    className="form-check-input"
                    type="checkbox"
                    checked={institutionDraft.active}
                    onChange={(event) =>
                      setInstitutionDraft((current) => ({ ...current, active: event.target.checked }))
                    }
                    disabled={institutionSaving}
                  />
                  <label htmlFor="create-institution-active" className="form-check-label">
                    Active
                  </label>
                </div>
              </div>
            </div>
            <div className="mt-3">
              <button
                className={`btn ${styles.darkActionButton}`}
                onClick={handleCreateInstitution}
                disabled={
                  institutionSaving ||
                  institutionDraft.name.trim().length < 2 ||
                  institutionDraft.edmProviderName.trim().length < 2 ||
                  !Number.isInteger(institutionDraft.sketchfabImportLimit) ||
                  institutionDraft.sketchfabImportLimit < 1 ||
                  institutionDraft.sketchfabImportLimit > 250
                }
              >
                Create Institution
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="row g-3 align-items-end mb-3">
        <div className="col-md-4">
          <label htmlFor="users-search" className="form-label text-white-50 small">
            Search users
          </label>
          <input
            id="users-search"
            aria-label="Search users"
            className={`form-control ${styles.lightInput}`}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search by name or email…"
            autoComplete="off"
            name="users-search"
          />
        </div>
        <div className="col-md-3">
          <label htmlFor="users-sort" className="form-label text-white-50 small">
            Sort users
          </label>
          <select
            id="users-sort"
            aria-label="Sort users"
            className={`form-select ${styles.lightSelect}`}
            value={sortValue}
            onChange={(event) => {
              const [nextSortBy, nextSortDirection] = event.target.value.split(":") as [
                AdminUsersDirectoryMeta["sortBy"],
                AdminUsersDirectoryMeta["sortDirection"]
              ];
              setSortValue(event.target.value);
              setSortBy(nextSortBy);
              setSortDirection(nextSortDirection);
              setPage(DEFAULT_DIRECTORY_PAGE);
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label htmlFor="users-page-size" className="form-label text-white-50 small">
            Users page size
          </label>
          <select
            id="users-page-size"
            aria-label="Users page size"
            className={`form-select ${styles.lightSelect}`}
            value={String(limit)}
            onChange={(event) => {
              const nextLimit = Number(event.target.value || DEFAULT_DIRECTORY_LIMIT);
              setLimit(nextLimit);
              setPage(DEFAULT_DIRECTORY_PAGE);
            }}
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 text-md-end">
          <div className="small text-white-50">Showing {showingFrom}-{showingTo} of {total}</div>
        </div>
      </div>

      <div className={styles.adminUsersTableScroller}>
        <table className={`table text-white table-striped table-hover mb-0 ${styles.adminUsersTable}`}>
          <caption className="visually-hidden">User gallery capacity and curator settings</caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Roles</th>
              <th scope="col">
                Curator{" "}
                <span
                  aria-label="Curator access rules"
                  title={CURATOR_NOTE}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "16px",
                    height: "16px",
                    borderRadius: "999px",
                    border: "1px solid currentColor",
                    fontSize: "11px",
                    lineHeight: 1,
                    cursor: "help",
                  }}
                >
                  i
                </span>
              </th>
              <th scope="col">Institution</th>
              <th scope="col">Galleries</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const roles = getUserRoles(user);
              const sellerRow = isSellerRole(roles) && !isAdminRole(roles);
              const adminRow = isAdminRole(roles);
              const draft = drafts[user._id] || getUserSettingsDraft(user);
              const selectedInstitution = institutions.find((entry) => entry._id === draft.institutionId) || null;
              const adminInstitutionName = resolveAdminInstitutionName(user);
              const hasChanges = isUserSettingsDraftDirty(draft, user);
              const institutionIsRequired = draft.curator && !draft.institutionId;
              const galleryLimitIsValid = isValidGalleryLimit(draft.galleryLimit);
              const galleryUsage = Number.isInteger(user.galleryUsage) && user.galleryUsage >= 0
                ? user.galleryUsage
                : 0;
              const persistedGalleryLimit = Number.isInteger(user.galleryLimit) && user.galleryLimit >= 1
                ? user.galleryLimit
                : null;
              const galleryIsOverLimit = persistedGalleryLimit !== null
                && galleryUsage > persistedGalleryLimit;
              const galleryLimitInputId = `gallery-limit-${user._id}`;
              const galleryUsageId = `gallery-usage-${user._id}`;
              const galleryLimitErrorId = `gallery-limit-error-${user._id}`;

              return (
                <tr key={user._id}>
                  <td className={styles.breakCell}>{user.name}</td>
                  <td className={styles.breakCell}>{user.email}</td>
                  <td className={styles.breakCell}>{roles.join(", ") || "Unknown"}</td>
                  <td>
                    {sellerRow ? (
                      <input
                        aria-label={`Curator for ${user.email}`}
                        type="checkbox"
                        checked={draft.curator}
                        onChange={(event) =>
                          handleUserSettingsDraftChange(user._id, { curator: event.target.checked })
                        }
                        disabled={savingUserId === user._id}
                      />
                    ) : adminRow ? (
                      "Always allowed"
                    ) : (
                      "Not applicable"
                    )}
                  </td>
                  <td className={styles.institutionCell}>
                    {sellerRow ? (
                      <div>
                        <div className={styles.institutionControlRow}>
                          <select
                            aria-label={`Institution for ${user.email}`}
                            className={`form-select form-select-sm ${styles.lightSelect}`}
                            value={draft.institutionId}
                            onChange={(event) =>
                              handleUserSettingsDraftChange(user._id, { institutionId: event.target.value })
                            }
                            disabled={savingUserId === user._id || institutionsLoading}
                          >
                            <option value="">Select institution…</option>
                            {institutions.map((institution) => (
                              <option key={institution._id} value={institution._id}>
                                {institution.name}
                              </option>
                            ))}
                          </select>
                          <button
                            className={`btn btn-sm ${styles.darkActionButton}`}
                            onClick={() => {
                              setInstitutionCreatorTargetUserId(user._id);
                              setInstitutionDraft(EMPTY_INSTITUTION_DRAFT);
                            }}
                            disabled={savingUserId === user._id}
                          >
                            New Institution
                          </button>
                        </div>
                        <div className="mt-1">
                          {selectedInstitution ? (
                            <small className="text-white-50">
                              Shared SRS limit: {selectedInstitution.sketchfabImportLimit}
                            </small>
                          ) : null}
                        </div>
                        {institutionIsRequired ? (
                          <small className="text-warning">
                            Institution assignment is required when curator access is enabled.
                          </small>
                        ) : null}
                      </div>
                    ) : adminRow ? (
                      <div className="small">
                        <div>{adminInstitutionName}</div>
                        <div className="text-white-50">Read-only for admin users</div>
                      </div>
                    ) : (
                      "Not applicable"
                    )}
                  </td>
                  <td className={styles.galleryLimitCell}>
                    <div className={styles.galleryLimitInline}>
                      <div id={galleryUsageId} className={styles.galleryLimitUsage}>
                        {galleryUsage} of {persistedGalleryLimit ?? "?"} used
                        {galleryIsOverLimit ? <span className="text-warning"> · Over limit</span> : null}
                      </div>
                      <label htmlFor={galleryLimitInputId} className={styles.galleryLimitLabel}>
                        Limit
                      </label>
                      <input
                        id={galleryLimitInputId}
                        aria-label={`Gallery Limit for ${user.email}`}
                        aria-describedby={`${galleryUsageId}${galleryLimitIsValid ? "" : ` ${galleryLimitErrorId}`}`}
                        className={`form-control form-control-sm ${styles.lightInput} ${styles.galleryLimitInput}`}
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        name={`gallery-limit-${user._id}`}
                        autoComplete="off"
                        value={draft.galleryLimit}
                        onChange={(event) =>
                          handleUserSettingsDraftChange(user._id, { galleryLimit: event.target.value })
                        }
                        disabled={savingUserId === user._id}
                        aria-invalid={!galleryLimitIsValid}
                      />
                    </div>
                    {!galleryLimitIsValid ? (
                      <small id={galleryLimitErrorId} className="text-warning d-block mt-1" aria-live="polite">
                        Use a whole number of at least 1.
                      </small>
                    ) : null}
                  </td>
                  <td>
                    <button
                      aria-label={`Save ${user.email}`}
                      aria-busy={savingUserId === user._id}
                      className="btn btn-sm btn-success"
                      onClick={() => handleSave(user)}
                      disabled={!hasChanges || savingUserId === user._id || !galleryLimitIsValid}
                    >
                      {savingUserId === user._id ? "Saving…" : "Save Settings"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {!usersLoading && users.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-white-50 py-4">
                  {directorySearch ? "No users match the current filters." : "No users available."}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3 gap-2 flex-wrap">
        <button
          className={`btn btn-sm ${styles.darkActionButton}`}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          disabled={usersLoading || usersMeta.page <= 1}
        >
          Previous
        </button>
        <div className="d-flex gap-2 align-items-center">
          {pageNumbers.map((pageNumber) => (
            <button
              key={pageNumber}
              className={`btn btn-sm ${pageNumber === usersMeta.page ? "btn-light" : styles.darkActionButton}`}
              onClick={() => setPage(pageNumber)}
              disabled={usersLoading || pageNumber === usersMeta.page}
            >
              {pageNumber}
            </button>
          ))}
        </div>
        <button
          className={`btn btn-sm ${styles.darkActionButton}`}
          onClick={() => setPage((current) => Math.min(usersMeta.totalPages, current + 1))}
          disabled={usersLoading || usersMeta.page >= usersMeta.totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};
